import React, { useState } from 'react';
import { Link, Search, Settings, Plus, X, Globe, Clock, UserCheck, ChevronDown, ChevronUp } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент формы для проверки URL и перенаправлений
 */
const RedirectCheckerForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isChecking, 
  onCheckUrl,
  httpMethods,
  proxyLocations,
  showAdvancedOptions,
  setShowAdvancedOptions
}) => {
  const [newHeader, setNewHeader] = useState({ name: '', value: '' });
  const [newCookie, setNewCookie] = useState({ name: '', value: '' });

  // Обработчик изменения основных полей формы
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Логика для зависимых полей
      if (name === 'useProxy' && !checked) {
        newState.proxyLocation = 'russia';
        newState.customProxy = '';
      }

      if (name === 'proxyLocation' && value !== 'custom') {
        newState.customProxy = '';
      }

      if (name === 'method' && value === 'HEAD') {
        newState.followRedirects = false;
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Добавление нового HTTP-заголовка
  const addHeader = () => {
    if (!newHeader.name || !newHeader.value) return;

    setFormData(prev => ({
      ...prev,
      headers: [...prev.headers, { ...newHeader }]
    }));
    setNewHeader({ name: '', value: '' });
  };

  // Удаление HTTP-заголовка
  const removeHeader = (index) => {
    setFormData(prev => ({
      ...prev,
      headers: prev.headers.filter((_, i) => i !== index)
    }));
  };

  // Добавление нового cookie
  const addCookie = () => {
    if (!newCookie.name || !newCookie.value) return;

    setFormData(prev => ({
      ...prev,
      cookies: [...prev.cookies, { ...newCookie }]
    }));
    setNewCookie({ name: '', value: '' });
  };

  // Удаление cookie
  const removeCookie = (index) => {
    setFormData(prev => ({
      ...prev,
      cookies: prev.cookies.filter((_, i) => i !== index)
    }));
  };

  // Валидация формы перед отправкой
  const validateForm = () => {
    const newErrors = {};

    // Проверка URL
    if (!formData.url.trim()) {
      newErrors.url = 'URL обязателен для заполнения';
    } else {
      const urlPattern = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
      if (!urlPattern.test(formData.url)) {
        newErrors.url = 'Введите корректный URL';
      }
    }

    // Проверка таймаута
    if (formData.timeout <= 0) {
      newErrors.timeout = 'Таймаут должен быть положительным числом';
    }

    // Проверка максимального количества редиректов
    if (formData.maxRedirects < 0) {
      newErrors.maxRedirects = 'Количество редиректов не может быть отрицательным';
    }

    // Проверка пользовательского прокси
    if (formData.useProxy && formData.proxyLocation === 'custom' && !formData.customProxy) {
      newErrors.customProxy = 'Введите адрес прокси-сервера';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Обработка отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      onCheckUrl();
    }
  };

  // Предзаполненные User-Agent строки
  const predefinedUserAgents = [
    { label: 'Chrome (Windows)', value: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/119.0.0.0 Safari/537.36' },
    { label: 'Chrome (Android)', value: 'Mozilla/5.0 (Linux; Android 10; SM-A205U) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/119.0.5359.128 Mobile Safari/537.36' },
    { label: 'Safari (iOS)', value: 'Mozilla/5.0 (iPhone; CPU iPhone OS 16_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/16.0 Mobile/15E148 Safari/604.1' },
    { label: 'Яндекс.Браузер', value: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/118.0.0.0 YaBrowser/23.11.0.0 Safari/537.36' },
    { label: 'Yandex Bot', value: 'Mozilla/5.0 (compatible; YandexBot/3.0; +http://yandex.com/bots)' },
    { label: 'Google Bot', value: 'Mozilla/5.0 (compatible; Googlebot/2.1; +http://www.google.com/bot.html)' }
  ];

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Link className="w-6 h-6 text-indigo-500" />
          Проверка перенаправлений URL
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* URL для проверки */}
        <div className="mb-6">
          <label htmlFor="url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Globe className="w-4 h-4 mr-2 text-indigo-500" />
            URL для проверки
          </label>
          <div className="flex">
            <input
              type="text"
              id="url"
              name="url"
              value={formData.url}
              onChange={handleInputChange}
              className={`form-input flex-grow ${errors.url ? 'input-error' : ''}`}
              placeholder="Введите URL (например, example.com)"
              disabled={isChecking}
            />
            <select
              name="method"
              value={formData.method}
              onChange={handleInputChange}
              className="form-select ml-2 w-24"
              disabled={isChecking}
            >
              {httpMethods.map((method) => (
                <option key={method.id} value={method.id}>
                  {method.name}
                </option>
              ))}
            </select>
          </div>
          {errors.url && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.url}
            </p>
          )}
        </div>

        {/* Основные опции */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="followRedirects"
                checked={formData.followRedirects}
                onChange={handleInputChange}
                className="form-checkbox"
                disabled={isChecking || formData.method === 'HEAD'}
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Следовать по редиректам
              </span>
            </label>
          </div>

          <div>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="validateSsl"
                checked={formData.validateSsl}
                onChange={handleInputChange}
                className="form-checkbox"
                disabled={isChecking}
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Проверять SSL-сертификаты
              </span>
            </label>
          </div>

          <div>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="includeHeaders"
                checked={formData.includeHeaders}
                onChange={handleInputChange}
                className="form-checkbox"
                disabled={isChecking}
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Включать HTTP-заголовки
              </span>
            </label>
          </div>

          <div>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="includeCookies"
                checked={formData.includeCookies}
                onChange={handleInputChange}
                className="form-checkbox"
                disabled={isChecking}
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Включать cookies
              </span>
            </label>
          </div>
        </div>

        {/* Кнопка для расширенных настроек */}
        <button
          type="button"
          onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
          className="flex items-center text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 mb-4"
        >
          <Settings className="w-4 h-4 mr-2" />
          {showAdvancedOptions ? 'Скрыть расширенные настройки' : 'Показать расширенные настройки'}
          {showAdvancedOptions ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
        </button>

        {/* Расширенные настройки */}
        {showAdvancedOptions && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4 space-y-6">
            {/* User-Agent */}
            <div>
              <label htmlFor="userAgent" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <UserCheck className="w-4 h-4 mr-2 text-indigo-500" />
                User-Agent
              </label>
              <div className="flex flex-col space-y-2">
                <input
                  type="text"
                  id="userAgent"
                  name="userAgent"
                  value={formData.userAgent}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="User-Agent строка"
                  disabled={isChecking}
                />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {predefinedUserAgents.slice(0, 3).map((ua, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, userAgent: ua.value }))}
                      className={`px-2 py-1 text-xs rounded border transition-colors
                        ${formData.userAgent === ua.value 
                          ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                          : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                        }`}
                    >
                      {ua.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Прокси-сервер */}
            <div>
              <div className="flex items-start">
                <div className="flex h-5 items-center">
                  <input
                    type="checkbox"
                    name="useProxy"
                    checked={formData.useProxy}
                    onChange={handleInputChange}
                    className="form-checkbox"
                    disabled={isChecking}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="useProxy" className="font-medium text-gray-700 dark:text-gray-300">
                    Использовать прокси-сервер
                  </label>
                  <p className="text-gray-500 dark:text-gray-400">
                    Проверка URL через прокси-сервер в выбранном регионе
                  </p>
                </div>
              </div>

              {formData.useProxy && (
                <div className="mt-2 pl-8 grid gap-3">
                  <div>
                    <label htmlFor="proxyLocation" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Локация прокси
                    </label>
                    <select
                      id="proxyLocation"
                      name="proxyLocation"
                      value={formData.proxyLocation}
                      onChange={handleInputChange}
                      className="form-select w-full"
                      disabled={isChecking}
                    >
                      {proxyLocations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formData.proxyLocation === 'custom' && (
                    <div>
                      <label htmlFor="customProxy" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Адрес прокси
                      </label>
                      <input
                        type="text"
                        id="customProxy"
                        name="customProxy"
                        value={formData.customProxy}
                        onChange={handleInputChange}
                        className={`form-input ${errors.customProxy ? 'input-error' : ''}`}
                        placeholder="http://proxy.example.com:8080"
                        disabled={isChecking}
                      />
                      {errors.customProxy && (
                        <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                          {errors.customProxy}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Таймаут и максимальное количество редиректов */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="timeout" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-indigo-500" />
                  Таймаут (мс)
                </label>
                <input
                  type="number"
                  id="timeout"
                  name="timeout"
                  value={formData.timeout}
                  onChange={handleInputChange}
                  className={`form-input ${errors.timeout ? 'input-error' : ''}`}
                  min="1000"
                  max="60000"
                  step="1000"
                  disabled={isChecking}
                />
                {errors.timeout && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.timeout}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="maxRedirects" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Макс. редиректов
                </label>
                <input
                  type="number"
                  id="maxRedirects"
                  name="maxRedirects"
                  value={formData.maxRedirects}
                  onChange={handleInputChange}
                  className={`form-input ${errors.maxRedirects ? 'input-error' : ''}`}
                  min="0"
                  max="20"
                  disabled={isChecking || !formData.followRedirects}
                />
                {errors.maxRedirects && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.maxRedirects}
                  </p>
                )}
              </div>
            </div>

            {/* HTTP-заголовки */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Пользовательские HTTP-заголовки
                </label>
              </div>
              
              {formData.headers.length > 0 && (
                <div className="mb-3 max-h-48 overflow-y-auto custom-scrollbar border border-gray-200 dark:border-gray-700 rounded-md p-2">
                  {formData.headers.map((header, index) => (
                    <div key={index} className="flex items-center mb-1 last:mb-0">
                      <div className="flex-grow flex items-center">
                        <span className="font-medium text-gray-700 dark:text-gray-300 text-sm">{header.name}: </span>
                        <span className="text-gray-600 dark:text-gray-400 text-sm ml-1">{header.value}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeHeader(index)}
                        className="text-red-500 hover:text-red-700 dark:hover:text-red-400 p-1"
                        disabled={isChecking}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex space-x-2 mt-2">
                <input
                  type="text"
                  placeholder="Имя заголовка"
                  value={newHeader.name}
                  onChange={(e) => setNewHeader({ ...newHeader, name: e.target.value })}
                  className="form-input flex-1"
                  disabled={isChecking}
                />
                <input
                  type="text"
                  placeholder="Значение"
                  value={newHeader.value}
                  onChange={(e) => setNewHeader({ ...newHeader, value: e.target.value })}
                  className="form-input flex-1"
                  disabled={isChecking}
                />
                <Button
                  type="button"
                  onClick={addHeader}
                  icon={<Plus className="w-4 h-4" />}
                  variant="outline"
                  disabled={isChecking || !newHeader.name || !newHeader.value}
                  size="sm"
                >
                  Добавить
                </Button>
              </div>
            </div>

            {/* Cookies */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Пользовательские cookies
                </label>
              </div>
              
              {formData.cookies.length > 0 && (
                <div className="mb-3 max-h-48 overflow-y-auto custom-scrollbar border border-gray-200 dark:border-gray-700 rounded-md p-2">
                  {formData.cookies.map((cookie, index) => (
                    <div key={index} className="flex items-center mb-1 last:mb-0">
                      <div className="flex-grow flex items-center">
                        <span className="font-medium text-gray-700 dark:text-gray-300 text-sm">{cookie.name}=</span>
                        <span className="text-gray-600 dark:text-gray-400 text-sm ml-1">{cookie.value}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeCookie(index)}
                        className="text-red-500 hover:text-red-700 dark:hover:text-red-400 p-1"
                        disabled={isChecking}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex space-x-2 mt-2">
                <input
                  type="text"
                  placeholder="Имя cookie"
                  value={newCookie.name}
                  onChange={(e) => setNewCookie({ ...newCookie, name: e.target.value })}
                  className="form-input flex-1"
                  disabled={isChecking}
                />
                <input
                  type="text"
                  placeholder="Значение"
                  value={newCookie.value}
                  onChange={(e) => setNewCookie({ ...newCookie, value: e.target.value })}
                  className="form-input flex-1"
                  disabled={isChecking}
                />
                <Button
                  type="button"
                  onClick={addCookie}
                  icon={<Plus className="w-4 h-4" />}
                  variant="outline"
                  disabled={isChecking || !newCookie.name || !newCookie.value}
                  size="sm"
                >
                  Добавить
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Кнопка отправки */}
        <div className="mt-8">
          <Button
            type="submit"
            primary
            icon={<Search className="w-5 h-5" />}
            disabled={isChecking}
            isLoading={isChecking}
            size="lg"
            fullWidth
          >
            {isChecking ? 'Проверяем...' : 'Проверить URL'}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default RedirectCheckerForm;